import { isReactMemoOrLazy } from './utils/isFunction';
import { createScrollShim } from './utils/scrollUtils';
import RowsContainer from './RowsContainer';
import RowGroup from './RowGroup';
import DropTargetRowContainer from '../react-data-grid-addons/src/draggable/DropTargetRowContainer';
import { CSSProperties, forwardRef, JSXElementConstructor, NamedExoticComponent, ReactElement } from 'react';
import EditableDataGridContext, { IEditableDataGridContext } from '../Context/EditableDataGridContext';
import { ICellMetaDataShape } from './PropTypeShapes/CellMetaDataShape';
import { IRowSelection } from './ReactDataGrid';
import { shallowEqual } from 'react-redux';

const React = require('react');

const joinClasses = require('classnames');

const Row = require('./Row');

const RowUtils = require('./RowUtils');

require('styles/react-data-grid/react-data-grid-core.css');

const CanvasRowWrapper = forwardRef(
  (rowProps: { row: any; canvasProps: any; idx: number; rowRenderer: any; rowGroupRenderer: any }, ref) => {
    const row = rowProps.row;

    if (row.__metaData && row.__metaData.getRowRenderer) {
      return row.__metaData.getRowRenderer(rowProps.canvasProps, rowProps.idx);
    }

    if (row.__metaData && row.__metaData.isGroup) {
      return (
        <RowGroup ref={ref} {...rowProps} {...row.__metaData} name={row.name} renderer={rowProps.rowGroupRenderer} />
      );
    }

    const RowsRenderer = rowProps.rowRenderer;

    if (typeof RowsRenderer === 'function' || isReactMemoOrLazy(RowsRenderer)) {
      return <RowsRenderer ref={ref} {...rowProps} />;
    }

    if (React.isValidElement(RowsRenderer)) {
      return React.cloneElement(RowsRenderer, rowProps);
    }
  }
);
const DropTargetRow = DropTargetRowContainer(CanvasRowWrapper);

export type ICanvasProps = {
  rowRenderer?: ReactElement | JSXElementConstructor<any> | unknown;
  rowHeight: number;
  height: number;
  width?: number;
  totalWidth: number | string;
  style?: string;
  className?: string;
  displayStart: number;
  displayEnd: number;
  visibleStart: number;
  visibleEnd: number;
  colVisibleStart: number;
  colVisibleEnd: number;
  colDisplayStart: number;
  colDisplayEnd: number;
  rowsCount: number;
  rowGetter: Array<any> | ((rowIndex: number) => any);
  expandedRows?: Array<any>;
  onRows?: () => void;
  onScroll: (scroll: { scrollTop: number; scrollLeft: number }) => void;
  columns: Array<any>;
  cellMetaData?: ICellMetaDataShape;
  selectedRows?: Array<any>;
  rowKey: string;
  rowScrollTimeout?: number;
  scrollToRowIndex?: number;
  contextMenu?: ReactElement;
  contextKey?: string;
  getSubRowDetails?: (row: any) => any;
  rowSelection: IRowSelection['selectBy'];
  rowGroupRenderer: any;
  isScrolling: boolean;
  length?: number;
  testId?: string;
  disableVirtualRows?: boolean;
};

type ICanvasState = {
  scrollingTimeout: null;
};

const INITIAL_ROW_RANGE = {
  start: 0,
  end: 0,
};

class Canvas extends React.Component<ICanvasProps, ICanvasState> {
  constructor(props: ICanvasProps) {
    super(props);
    this.rows = [];
    this._currentRowsLength = 0;
    this._currentRowsRange = INITIAL_ROW_RANGE;
    this._scroll = {
      scrollTop: 0,
      scrollLeft: 0,
    };
    this.state = {
      scrollingTimeout: null,
    };
  }

  static displayName = 'Canvas';
  appendScrollShim = () => {
    if (!this._scrollShim) {
      const size = this._scrollShimSize();

      const shim = createScrollShim(size);
      this.canvas.appendChild(shim);
      this._scrollShim = shim;
    }

    this._scheduleRemoveScrollShim();
  };
  _scrollShimSize = (): {
    width: number;
    height: number;
  } => {
    return {
      width: this.props.width,
      height: this.props.length * this.props.rowHeight,
    };
  };
  _scheduleRemoveScrollShim = () => {
    if (this._scheduleRemoveScrollShimTimer) {
      clearTimeout(this._scheduleRemoveScrollShimTimer);
    }

    this._scheduleRemoveScrollShimTimer = setTimeout(this._removeScrollShim, 200);
  };
  _removeScrollShim = () => {
    if (this._scrollShim) {
      this._scrollShim.parentNode.removeChild(this._scrollShim);

      this._scrollShim = undefined;
    }
  };

  componentDidMount() {
    this.onRows();
  }

  shouldComponentUpdate(nextProps: ICanvasProps, nextState: ICanvasState, nextContext: IEditableDataGridContext) {
    return (
      nextProps.displayStart !== this.props.displayStart ||
      nextProps.displayEnd !== this.props.displayEnd ||
      nextState.scrollingTimeout !== this.state.scrollingTimeout ||
      this.props.scrollToRowIndex !== nextProps.scrollToRowIndex ||
      nextProps.rowsCount !== this.props.rowsCount ||
      nextProps.rowHeight !== this.props.rowHeight ||
      nextProps.columns !== this.props.columns ||
      nextProps.width !== this.props.width ||
      nextProps.height !== this.props.height ||
      nextProps.cellMetaData !== this.props.cellMetaData ||
      this.props.colDisplayStart !== nextProps.colDisplayStart ||
      this.props.colDisplayEnd !== nextProps.colDisplayEnd ||
      this.props.colVisibleStart !== nextProps.colVisibleStart ||
      this.props.colVisibleEnd !== nextProps.colVisibleEnd ||
      !shallowEqual(nextProps.style, this.props.style) ||
      this.props.isScrolling !== nextProps.isScrolling
    );
  }

  componentWillUnmount() {
    this._currentRowsLength = 0;
    this._currentRowsRange = INITIAL_ROW_RANGE;
    this._scroll = {
      scrollTop: 0,
      scrollLeft: 0,
    };
  }

  componentDidUpdate() {
    if (this._scroll.scrollTop !== 0 && this._scroll.scrollLeft !== 0) {
      this.setScrollLeft(this._scroll.scrollLeft);
    }

    if (!this.props.disableVirtualRows && this.props.scrollToRowIndex !== 0) {
      this.canvas.scrollTop = Math.min(
        this.props.scrollToRowIndex * this.props.rowHeight,
        this.props.rowsCount * this.props.rowHeight - this.props.height
      );
    }

    this.onRows();
  }

  onRows = () => {
    if (this._currentRowsRange !== INITIAL_ROW_RANGE) {
      this.props.onRows(this._currentRowsRange);
      this._currentRowsRange = INITIAL_ROW_RANGE;
    }
  };
  onScroll = (e: any) => {
    if (this.canvas !== e.target || this.props.disableVirtualRows) {
      return;
    }

    this.appendScrollShim();
    const scrollLeft = e.target.scrollLeft;
    const scrollTop = e.target.scrollTop;
    const scroll = {
      scrollTop,
      scrollLeft,
    };
    this._scroll = scroll;
    this.props.onScroll(scroll);
  };
  getRows = (displayStart: number, displayEnd: number) => {
    this._currentRowsRange = {
      start: displayStart,
      end: displayEnd,
    };

    if (Array.isArray(this.props.rowGetter)) {
      return this.props.rowGetter.slice(displayStart, displayEnd);
    }

    const rows: Array<{ row: any; subRowDetails: any }> = [];
    let i = displayStart;

    while (i < displayEnd && i < this.props.rowsCount) {
      const row = this.props.rowGetter(i);
      let subRowDetails = {};

      if (this.props.getSubRowDetails) {
        subRowDetails = this.props.getSubRowDetails(row);
      }

      rows.push({
        row,
        subRowDetails,
      });
      i++;
    }

    return rows;
  };
  getScrollbarWidth = () => {
    // Get the scrollbar width
    return this.canvas.offsetWidth - this.canvas.clientWidth;
  };
  getScroll = () => {
    const { scrollTop, scrollLeft } = this.canvas;
    return {
      scrollTop,
      scrollLeft,
    };
  };
  isRowSelected = (idx: number, row: any) => {
    // Use selectedRows if set [DetailDataGrid]
    if (this.props.selectedRows !== null) {
      const selectedRows = this.props.selectedRows.filter((r: any) => {
        const rowKeyValue = row.get ? row.get(this.props.rowKey) : row[this.props.rowKey];
        return r[this.props.rowKey] === rowKeyValue;
      });
      return selectedRows.length > 0 && selectedRows[0].isSelected;
    }

    // Else use new rowSelection props [EditableDataGrid]
    // UUID keyed for persistence across different generic filters.
    // Requires additional configuration above EditableDataGrid, i.e. WorkspaceGraphQL.
    if (this.props.rowSelection) {
      const { keys, indexes, isSelectedKey, all } = this.props.rowSelection;
      const selected = RowUtils.isRowSelected(keys, indexes, isSelectedKey, row, idx);
      return all ? !selected : selected;
    }

    return false;
  };
  setScrollLeft = (scrollLeft: any) => {
    if (this._currentRowsLength !== 0) {
      if (!this.rows) return;

      for (let i = 0, len = this._currentRowsLength; i < len; i++) {
        if (this.rows[i]) {
          const row = this.getRowByRef(i);

          if (row && row.setScrollLeft) {
            row.setScrollLeft(scrollLeft);
          }
        }
      }
    }
  };
  getRowByRef = (i: number) => {
    // check if wrapped with React DND drop target
    const wrappedRow = this.rows[i].getDecoratedComponentInstance
      ? this.rows[i].getDecoratedComponentInstance(i)
      : null;

    if (wrappedRow) {
      return wrappedRow.row;
    }

    return this.rows[i];
  };
  renderPlaceholder = (key: string, height: number) => {
    // just renders empty cells
    // if we wanted to show gridlines, we'd need classes and position as with renderScrollingPlaceholder
    return (
      <div
        key={key}
        style={{
          height: height,
        }}
      >
        {this.props.columns.map((column: any, idx: number) => {
          return (
            <div
              style={{
                width: column.width,
              }}
              key={idx as unknown as number}
            />
          );
        })}
      </div>
    );
  };

  render() {
    const { rowHeight, rowsCount, displayStart, displayEnd } = this.props;
    const { onRowSort } = this.context;
    const rows = this.getRows(displayStart, displayEnd).map((r: any, idx: number) => {
      const key = r.row?.uuid ?? `row-${displayStart + idx}`;
      const isSelected = this.isRowSelected(displayStart + idx, r.row);
      return (
        <DropTargetRow
          key={key}
          ref={(node) => (this.rows[idx] = node)}
          idx={displayStart + idx}
          visibleStart={this.props.visibleStart}
          visibleEnd={this.props.visibleEnd}
          row={r.row}
          height={rowHeight}
          onMouseOver={this.onMouseOver}
          columns={this.props.columns}
          isSelected={isSelected}
          expandedRows={this.props.expandedRows}
          cellMetaData={this.props.cellMetaData}
          subRowDetails={r.subRowDetails}
          colVisibleStart={this.props.colVisibleStart}
          colVisibleEnd={this.props.colVisibleEnd}
          colDisplayStart={this.props.colDisplayStart}
          colDisplayEnd={this.props.colDisplayEnd}
          isScrolling={this.props.isScrolling}
          testId={this.props.testId}
          rowRenderer={this.props.rowRenderer}
          canvasProps={this.props}
          onRowDrop={onRowSort}
        />
      );
    });
    this._currentRowsLength = rows.length;

    if (displayStart > 0) {
      rows.unshift(this.renderPlaceholder('top', displayStart * rowHeight));
    }

    if (rowsCount - displayEnd > 0) {
      rows.push(this.renderPlaceholder('bottom', (rowsCount - displayEnd) * rowHeight));
    }

    const style: CSSProperties = {
      position: 'absolute',
      top: 0,
      left: 0,
      overflowX: 'auto',
      overflowY: 'auto',
      width: this.props.totalWidth,
      height: this.props.height,
    };
    return (
      <div
        ref={(div) => {
          this.canvas = div;
        }}
        style={style}
        onScroll={this.onScroll}
        className={joinClasses('react-grid-Canvas', this.props.className, {
          opaque: this.props.cellMetaData.selected && this.props.cellMetaData.selected.active,
        })}
      >
        <RowsContainer
          rows={rows}
          contextMenu={this.props.contextMenu}
          contextKey={this.props.contextKey}
          rowIdx={this.props.cellMetaData.selected.rowIdx}
          idx={this.props.cellMetaData.selected.idx}
        />
      </div>
    );
  }
}

Canvas.contextType = EditableDataGridContext;
Canvas.defaultProps = {
  rowRenderer: Row,
  onRows: () => {},
  selectedRows: [],
  rowScrollTimeout: 0,
};

//This type hack shouldn't be necessary (Canvas is a valid Component) but Viewport doesn't recognize its type as valid for some reason, so it'll do...
export default Canvas as unknown as NamedExoticComponent<ICanvasProps & { ref: any }>;
